import React, {
  useContext,
  useRef,
  useEffect,
  useState,
  ChangeEvent,
} from 'react';
import styled from 'astroturf/react';

import $image_url from 'js/utils/image_url';

import { ResearchInterest } from './types';
import ResearchInterestSearchResults from './ResearchInterestSearchResults';

interface Props {
  query: string;
  onQueryInput(e: ChangeEvent<HTMLInputElement>): void;
  isLoading: boolean;
  selectedResearchInterests: Array<ResearchInterest>;
  searchResults: Array<ResearchInterest>;
  onAdd(ri: ResearchInterest): void;
  onRemove(ri: ResearchInterest): void;
  onCloseSearchResults(): void;
  maxResearchInterests: number;
  requiredText?: string;
  disabled?: boolean;
  hasError: boolean;
}

const NonFocusedColor = '#949494';
const FocusedColor = '#0954d3';
const RequiredTextHeight = '20px';

const defaultTheme: {
  border: React.CSSProperties;
  borderHighlighted: React.CSSProperties;
  inputContainer: React.CSSProperties;
  remainingResearchInterests: React.CSSProperties;
  remainingResearchInterestsHighlighted: React.CSSProperties;
} = {
  border: {
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: NonFocusedColor,
    borderRadius: '3px',
  },
  borderHighlighted: {
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: FocusedColor,
  },
  inputContainer: {
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: NonFocusedColor,
  },
  remainingResearchInterests: {
    color: NonFocusedColor,
  },
  remainingResearchInterestsHighlighted: {
    color: FocusedColor,
  },
};

export const ThemeContext = React.createContext(defaultTheme);

const Container = styled.div`
  font-size: 16px;
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: #ffffff;
`;

const Border = styled.div<{ hasError?: boolean }>`
  &.hasError {
    border-color: rgba(230, 36, 25, 1);
  }
`;

const RequiredText = styled.div`
  height: ${RequiredTextHeight};
  color: #595959;
  font-size: 12px;
  letter-spacing: 0.3px;
  text-indent: 11px;
  font-weight: 400;

  @media screen and (min-width: 992px) {
    order: -1;
    margin-top: -${RequiredTextHeight};
  }
`;

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  height: 40px;
  width: 100%;
  padding: 0px 5px;

  i {
    width: 16px;
    height: 16px;
    margin-right: 5px;
  }
`;

const SearchIcon = styled.img`
  width: 12px;
  height: 12px;
`;

const Input = styled.input`
  padding: 5px;
  border: none;
  margin-right: 5px;
  flex-grow: 2;
`;

const SelectedTagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  padding: 10px;
  height: 133px;
  overflow-x: hidden;
  overflow-y: auto;
`;

const RemainingResearchInterests = styled.div`
  text-align: right;
  padding: 5px;
  font-size: 12px;
  height: 25px;
`;

const SelectedResearchInterestContainer = styled.div`
  padding: 5px 10px;
  height: 30px;
  border: 1px solid #ddd;
  margin: 5px 10px 0 0;
  font-size: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SelectedResearchInterestName = styled.span`
  max-width: 150px;
  height: 20px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RemoveResearchInterestButton = styled.button`
  border: none;
  background: none;
`;

export function ResearchInterestTags(props: Props) {
  const {
    selectedResearchInterests,
    query,
    onQueryInput,
    searchResults,
    onAdd,
    onRemove,
    isLoading,
    onCloseSearchResults,
    maxResearchInterests,
    requiredText,
    disabled = false,
    hasError,
  } = props;

  const boxWrapper = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  const theme = useContext(ThemeContext);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'contains' does not exist on type 'never'... Remove this comment to see the full error message
      if (!boxWrapper?.current?.contains(e.target)) {
        // Close the search results if the user clicks outside of the text input
        // or the search results box.
        onCloseSearchResults();
      }
    };

    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
    // NATIONAL PARKS: Please remove this disable and try to fix it if you're here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boxWrapper]);

  const selected = selectedResearchInterests.map((ri) => (
    <SelectedResearchInterestContainer key={ri.id}>
      <SelectedResearchInterestName title={ri.displayName}>
        {ri.displayName}&nbsp;&nbsp;
      </SelectedResearchInterestName>
      <RemoveResearchInterestButton
        onClick={() => onRemove(ri)}
        disabled={disabled}
      >
        <i className="fa fa-times" />
      </RemoveResearchInterestButton>
    </SelectedResearchInterestContainer>
  ));

  return (
    <Container>
      <Border
        hasError={hasError}
        style={isFocused ? theme.borderHighlighted : theme.border}
      >
        <div ref={boxWrapper}>
          <InputContainer style={theme.inputContainer}>
            <SearchIcon src={$image_url('upload_funnel/search-icon.svg')} />
            <Input
              type="text"
              disabled={
                disabled ||
                selectedResearchInterests.length >= maxResearchInterests
              }
              value={query}
              onChange={onQueryInput}
              placeholder="Add research interests"
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
            />
            {isLoading && <i className="fa fa-spinner fa-spin" />}
          </InputContainer>

          {searchResults.length > 0 && (
            <ResearchInterestSearchResults
              researchInterests={searchResults}
              onAdd={onAdd}
            />
          )}
        </div>

        <SelectedTagsContainer>{selected}</SelectedTagsContainer>

        <RemainingResearchInterests
          style={
            isFocused
              ? theme.remainingResearchInterestsHighlighted
              : theme.remainingResearchInterests
          }
        >
          {selectedResearchInterests.length} / {maxResearchInterests}
        </RemainingResearchInterests>
      </Border>

      {requiredText && <RequiredText>{requiredText}</RequiredText>}
    </Container>
  );
}
